import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { TimePicker, DatePicker } from 'react-materialize';
import { Label } from '../atoms/label.jsx';
import Button from '../atoms/button.jsx';
import TextInput from '../atoms/textinput.jsx';
import Select from '../atoms/select.jsx';
import Row from '../organism/row.jsx';
import Banner from '../organism/banner.jsx';
import LoadingSpinner from '../organism/spinner.jsx';
import { COLOR, SIZE, FONT_FAMILY, SCREEN_MEDIA, LABEL } from '../../utils/constants.js';
import { useBookingDetail } from '../hooks/useBookingDetail.js';
import { bookingRepository } from '../../../domain/bookingRepository';
import { useBookingData } from '../hooks/useBookingData.js';
import { useDecorationTypes } from '../hooks/useDecorationTypes.js';
import {
    getEditOrganizerMantadoryFields,
    getEditEventMandatoryFields,
    getEditAllMandatoryFields,
    parseEventTime,
    toEventDateTimeUTC,
    toLocalDateTimeParts,
    EVENT_DATEPICKER_I18N
} from '../../utils/managereventbooking.js';

const PERU_TIME_ZONE = 'America/Lima';
const EMPTY_VALUE = '-';

const SECTION = {
    event: 'event',
    organizer: 'organizer'
};

const Container = styled.div`
    overflow: auto; // To cover height with contents
    padding: 0px 15px;
    @media only screen and ${SCREEN_MEDIA.small} {
        margin: 0px 2px;
    }
    @media only screen and ${SCREEN_MEDIA.medium} {
        margin: 30px 80px;
    }
    @media only screen and ${SCREEN_MEDIA.long} {
        margin: 30px 150px;
    }
    @media only screen and ${SCREEN_MEDIA.xlong} {
        margin: 30px 250px;
    }
`;

const DetailCard = styled.div`
    overflow: auto; // To cover height with contents
    padding: 25px;
    background-color: ${COLOR.background};
    border-radius: 25px;
    -webkit-box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
    -moz-box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
    box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
`;

const Section = styled.div`
    margin: 30px 0 0 0;
    padding: 20px 0 0 0;
    border-top: 2px solid ${COLOR.grayClear};
`;

// Section title on the left, edit button on the top-right corner.
const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
`;

const SectionActions = styled.div`
    display: flex;
    gap: 10px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px 15px;
    background-color: rgba(0, 0, 0, 0.5);
`;

const ModalCard = styled.div`
    width: 100%;
    max-width: 450px;
    padding: 25px;
    text-align: center;
    background-color: ${COLOR.background};
    border-radius: 25px;
    box-shadow: -1px 0px 22px -3px rgba(153,153,153,1);
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: center;
`;

// Label / value pairs: two columns on wider screens, stacked on phones.
const DetailRow = styled.div`
    display: grid;
    grid-template-columns: 220px 1fr;
    column-gap: 20px;
    align-items: baseline;
    @media only screen and ${SCREEN_MEDIA.small} {
        grid-template-columns: 1fr;
    }
    p {
        overflow-wrap: anywhere; // Long addresses / ids must not overflow the card
    }
`;

const PaymentImage = styled.img`
    display: block;
    max-width: 100%;
    max-height: 500px;
    margin: 15px 0 0 0;
    border: 2px solid ${COLOR.ferra};
    border-radius: ${SIZE.inp_radious};
`;

// A plain 'YYYY-MM-DD' (SQL DATE) has no time part, so it's shown as-is:
// `new Date('YYYY-MM-DD')` would parse it as UTC midnight and, in Peru's
// UTC-5, render it as the previous day.
const formatDate = (value) => {
    if (!value) return EMPTY_VALUE;
    const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return new Date(Number(year), Number(month) - 1, Number(day))
            .toLocaleDateString('es-PE', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    const date = new Date(value);
    if (isNaN(date)) return String(value);
    return date.toLocaleDateString('es-PE', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: PERU_TIME_ZONE
    });
};

const formatDateTime = (value) => {
    if (!value) return EMPTY_VALUE;
    const date = new Date(value);
    if (isNaN(date)) return String(value);
    return date.toLocaleString('es-PE', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true, timeZone: PERU_TIME_ZONE
    });
};

// SQL TIME ('18:00:00') -> '6:00 PM'. Anything else is shown untouched.
const formatTime = (value) => {
    if (!value) return EMPTY_VALUE;
    const time = String(value).match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!time) return String(value);
    const hours = Number(time[1]);
    const amOrPm = hours >= 12 ? 'PM' : 'AM';
    return `${(hours % 12) || 12}:${time[2]} ${amOrPm}`;
};

const formatCashAmount = (value) => {
    if (value === null || value === undefined || value === '') return EMPTY_VALUE;
    const amount = Number(value);
    return isNaN(amount) ? String(value) : `S/. ${amount.toFixed(2)}`;
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';


// The booking detail only carries the type ids: titles come from the event types list.
const getTypeTitles = (types, typeCatLv1Id, typeCatLv2Id) => {
    const typeCatLv1 = (types?.categoriesLv1 ?? []).find(item => String(item.id) === String(typeCatLv1Id));
    const typeCatLv2 = (typeCatLv1?.categoriesLv2 ?? []).find(item => String(item.id) === String(typeCatLv2Id));
    return {
        typeCatLv1Title: typeCatLv1?.title ?? null,
        typeCatLv2Title: typeCatLv2?.title ?? null
    };
};

// Same rule as formatDate: a plain 'YYYY-MM-DD' is read as local midnight
// (the DatePicker's own convention) instead of UTC midnight.
const parseDate = (value) => {
    if (!value) return null;
    const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
        const [, year, month, day] = dateOnly;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const date = new Date(value);
    return isNaN(date) ? null : date;
};

// A booking can be edited until EDIT_DEADLINE_DAYS before the event starts.
const EDIT_DEADLINE_DAYS = 3;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Date.now() return timestamp: 1791126903256
const isEditWindowOpen = (booking) => {
    const eventTimestamp = Date.parse(booking?.eventDateTime ?? '');
    console.log(`JE_TEST IS EDIT booking?.eventDateTime: ${booking?.eventDateTime}`)
    console.log(`JE_TEST IS EDIT eventTimestamp: ${eventTimestamp}`)
    console.log(`JE_TEST IS EDIT Date.now(): ${Date.now()}`)

    // Date.parse returns NaN if the string is invalid
    if (Number.isNaN(eventTimestamp)) {
        return false;
    }
    return eventTimestamp - Date.now() > EDIT_DEADLINE_DAYS * MS_PER_DAY;
};

// The booking can be checked until VIEW_AFTER_EVENT_DAYS after the event starts.
const VIEW_AFTER_EVENT_DAYS = 1;

// An unknown event start doesn't block the page (editing stays locked anyway).
const isAnOldBooking = (booking) => {
    const eventTimestamp = Date.parse(booking?.eventDateTime ?? '');
    if (Number.isNaN(eventTimestamp)) {
        return false;
    }
    return Date.now() - eventTimestamp > VIEW_AFTER_EVENT_DAYS * MS_PER_DAY;
};

// Whole booking form sent on save (organizer + event; payment can't be edited),
// in the same shape as the create flow: date and time go as one `eventDateTime`.
const buildBookingFormData = (form) => {
    const body = new FormData();
    const editAllMandatoryFields = getEditAllMandatoryFields(form.typeCatLv1);
    Object.entries(form).forEach(([name, value]) => {
        if (name === 'eventDate' || name === 'eventTime') return;
        if (name === 'placeName') { // Always sent. May be empty or non-empty value, never null.
            body.append(name, normalizeDraftValue(value));
            return;
        }
        if (name === 'typeCatLv2' && [3, 4, 6].includes(Number(form.typeCatLv1))) { // May be empty, non-empty or never sent.
            body.append(name, normalizeDraftValue(value));
            return;
        }
        if (editAllMandatoryFields.includes(name)) {
            body.append(name, normalizeDraftValue(value));
        }
    });
    const eventTime = parseEventTime(form.eventTime);
    if (form.eventDate instanceof Date && eventTime) {
        body.append('eventDateTime', toEventDateTimeUTC(form.eventDate, eventTime));
    }
    return body;
};

// Draft values are strings, except eventDate (a Date picked in the DatePicker).
const normalizeDraftValue = (value) => {
    if (value instanceof Date) return value;
    return String(value ?? '').trim();
};

const isSameDraftValue = (a, b) => {
    if (a instanceof Date || b instanceof Date) {
        return a instanceof Date && b instanceof Date && a.toDateString() === b.toDateString();
    }
    return a === b;
};

const SectionButton = ({ bg_color, text_color, disabled, onClick, children }) => (
    <Button
        type='button'
        height='36px'
        bg_color={ bg_color }
        disabled={ disabled }
        onClick={ onClick }>
        <Label
            text_color={ text_color }
            text_size={SIZE.body}
            font_family={FONT_FAMILY.avenirMedium}
            margin='auto'>
            { children }
        </Label>
    </Button>
);

// While a section is being edited, its Editar button turns into Cancelar / Guardar.
// showEdit={false} hides Editar for sections that can't be edited (Pago).
const SectionTitle = (
    {
        children,
        showEdit = true,
        isEditing,
        isSaving,
        isEditDisabled,
        onEdit,
        onCancel,
        onSave
    }
) => (
    <SectionHeader>
        <Label
            margin='0 0 10px 0'
            text_size={SIZE.subtitle}
            text_color={COLOR.ferra}
            font_family={FONT_FAMILY.avenirBlack}>
            { children }
        </Label>
        { isEditing
            ? <SectionActions>
                <SectionButton
                    bg_color={ COLOR.grayClear }
                    text_color={ COLOR.ferra }
                    disabled={ isSaving }
                    onClick={ onCancel }>
                    {LABEL.cancel}
                </SectionButton>
                <SectionButton
                    bg_color={ COLOR.red }
                    text_color={ COLOR.white }
                    disabled={ isSaving }
                    onClick={ onSave }>
                    { isSaving ? LABEL.saving : LABEL.save }
                </SectionButton>
            </SectionActions>
            : showEdit && <SectionButton
                bg_color={ COLOR.red }
                text_color={ COLOR.white }
                disabled={ isEditDisabled }
                onClick={ onEdit }>
                {LABEL.edit}
            </SectionButton> }
    </SectionHeader>
);

const MessageModal = ({ children, onClose }) => (
    <ModalOverlay>
        <ModalCard role='dialog' aria-modal='true'>
            <Label
                margin='0 0 20px 0'
                text_size={SIZE.subtitle}
                text_color={COLOR.ferra}
                font_family={FONT_FAMILY.avenirMedium}>
                { children }
            </Label>
            <ModalActions>
                <SectionButton
                    bg_color={ COLOR.primaryDark }
                    text_color={ COLOR.white }
                    onClick={ onClose }>
                    Ok
                </SectionButton>
            </ModalActions>
        </ModalCard>
    </ModalOverlay>
);

const NameVerificationHint = () => (
    <Label
        margin='0 0'
        text_size={SIZE.body}
        text_color={COLOR.secondary}
        font_family={FONT_FAMILY.avenirBlack}>
        Verifique que el nombre esté bien escrito, se usará para personalizar el evento.
    </Label>
);

const EmptyFieldMessage = () => <ErrorMessage>Por favor, complete este campo.</ErrorMessage>;

const ErrorMessage = ({ children }) => (
    <Label
        margin='0 0'
        text_size={SIZE.body}
        text_color={COLOR.secondaryDark}
        font_family={FONT_FAMILY.avenirMedium}>
        { children }
    </Label>
);

const Detail = ({ title, value }) => (
    <DetailRow>
        <Label
            margin='8px 0'
            text_size={SIZE.body}
            text_color={COLOR.grayDark}
            font_family={FONT_FAMILY.avenirMedium}>
            { title }
        </Label>
        <Label
            margin='8px 0'
            text_size={SIZE.body}
            text_color={COLOR.black}
            font_family={FONT_FAMILY.avenirMedium}>
            { hasValue(value) ? value : EMPTY_VALUE }
        </Label>
    </DetailRow>
);

const BodyBookingDetail = () => {

    const { bookingId } = useParams();
    const { data: booking, isLoading: isBookingLoading, error } = useBookingDetail(bookingId);
    const { data: decorationTypes, isLoading: isTypesLoading, error: typesError } = useDecorationTypes();

    // Only one section is edited at a time; its unsaved values live in `draft`.
    const [editingSection, setEditingSection] = useState(null);
    const [draft, setDraft] = useState({});
    const [initialDraft, setInitialDraft] = useState({});
    const [emptyFields, setEmptyFields] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showSavedModal, setShowSavedModal] = useState(false);

    const typeCatLv1List = decorationTypes?.categoriesLv1 ?? [];

    const buildOrganizerDraft = () => ({
        organizerName: String(booking.organizerName ?? ''),
        phone: String(booking.phone ?? '')
    });

    const buildEventDraft = () => {
        const event = toLocalDateTimeParts(booking.eventDateTime);
        return {
            typeCatLv1: String(booking.typeCatLv1Id ?? ''),
            typeCatLv2: String(booking.typeCatLv2Id ?? ''),
            birthNumber: String(booking.birthNumber ?? ''),
            celebrantName: String(booking.celebrantName ?? ''),
            couplePerson1Name: String(booking.couplePerson1Name ?? ''),
            couplePerson2Name: String(booking.couplePerson2Name ?? ''),
            eventDate: parseDate(event.date),
            eventTime: hasValue(event.time) ? formatTime(event.time) : '',
            address: String(booking.eventAddress ?? ''),
            placeName: String(booking.eventPlace ?? '')
        };
    };

    const startEditing = (section, fields) => {
        setEditingSection(section);
        setDraft(fields);
        setInitialDraft(fields);
        setEmptyFields({});
        setSaveError(null);
    };

    const cancelEditing = () => {
        setEditingSection(null);
        setDraft({});
        setInitialDraft({});
        setEmptyFields({});
        setSaveError(null);
    };

    // Stable identity: the pickers' callbacks/options below depend on it.
    const changeDraftField = useCallback((name, value) => {
        console.log(`JE_TEST changeDraftField 1 name, value: ${name}, ${value}`)
        setDraft(prevDraft => {
            console.log(`JE_TEST changeDraftField 1 prevDraft: ${JSON.stringify(prevDraft)}`)
            return { ...prevDraft, [name]: value }
        });
        setEmptyFields(prevEmptyFields => ({ ...prevEmptyFields, [name]: false })); // To clean empty message bellow all inputs
    }, []);

    const changeTypeCatLv1 = (value) => {
        // The subtype list depends on the type, so the old subtype no longer applies.
        setDraft(prevDraft => ({ ...prevDraft, typeCatLv1: value, typeCatLv2: '' }));
        setEmptyFields({});
    };

    // react-materialize re-inits the pickers whenever `options`/`onChange`
    // change identity (closing them mid-interaction), so both stay memoized;
    // see the same pickers in bodybookingcreate.jsx.
    const today = useMemo(() => new Date(), []);
    const initialEventDate = initialDraft.eventDate ?? null;

    const eventDatePickerOptions = useMemo(() => ({
        format: 'dd mmm yyyy',
        minDate: today,
        defaultDate: initialEventDate,
        setDefaultDate: Boolean(initialEventDate),
        i18n: EVENT_DATEPICKER_I18N
    }), [today, initialEventDate]);

    // Time picker onChange does not work propertly
    const eventTimePickerOptions = useMemo(() => ({
        twelveHour: true,
        defaultTime: '6:00 PM',
        onCloseEnd: function () { // COVERS PICKING FROM CLOCK
            changeDraftField('eventTime', this.el.value);
        }
    }), [changeDraftField]);

    const handleEventDateChange = useCallback(
        (date) => changeDraftField('eventDate', date),
        [changeDraftField]
    );

    const saveDraft = async () => {
        const isEvent = editingSection === SECTION.event;
        const currentDraft = draft;

        const newEmptyFields = {}
        const mandatoryFields = isEvent ? getEditEventMandatoryFields(currentDraft.typeCatLv1) : getEditOrganizerMantadoryFields()
        mandatoryFields.forEach((name) => {
            const rawValue = currentDraft[name]
            const isEmpty = rawValue === undefined || rawValue === null || String(rawValue).trim() === '';
            if (isEmpty) {
                newEmptyFields[name] = true;
            }
        });
        // PREVENTING WRONG FORMAT FOR eventTime. USER MAY WRITE ANYTHING. Ex. 7, 8pm
        if (isEvent && !newEmptyFields.eventTime && !parseEventTime(currentDraft.eventTime)) {
            newEmptyFields.eventTime = true;
        }

        console.log(`JE_TEST saveDraft mandatoryFields: ${JSON.stringify(mandatoryFields)}`)
        console.log(`JE_TEST saveDraft currentDraft: ${JSON.stringify(currentDraft)}`)
        console.log(`JE_TEST saveDraft initialDraft: ${JSON.stringify(initialDraft)}`)
        console.log(`JE_TEST saveDraft: newEmptyFields ${JSON.stringify(newEmptyFields)}`)

        setEmptyFields(newEmptyFields);
        if (Object.values(newEmptyFields).some(Boolean)) return;

        // Nothing changed against the values the form opened with: close without calling the endpoint.
        const hasChanges =  Object.keys(currentDraft).some(
            name => !isSameDraftValue(normalizeDraftValue(currentDraft[name]), normalizeDraftValue(initialDraft[name]))
        );
        if (!hasChanges) {
            cancelEditing();
            return;
        }

        // building whole booking, except payment data
        const body = buildBookingFormData({
            ...buildOrganizerDraft(),
            ...buildEventDraft(),
            ...currentDraft
        });
        console.log('JE_TEST body:', [...body.entries()]);

        setIsSaving(true);
        setSaveError(null);
        const result = await bookingRepository.updateBooking(bookingId, body);
        setIsSaving(false);
        if (!result.success) {
            setSaveError('No se pudieron guardar los cambios. Inténtalo nuevamente.');
            return;
        }
        // The booking is not updated locally: the backend sends the changes to the
        // team on Telegram, and the database is only updated once they accept them.
        cancelEditing();
        setShowSavedModal(true);
    };

    // Section header props: whether this section is the one being edited, and
    // Editar disabled on the rest so only one section has unsaved changes.
    // Editar is also disabled once the edit window before the event has closed.
    const editProps = (section, fields) => ({
        isEditing: editingSection === section,
        isSaving,
        isEditDisabled: editingSection !== null || !isEditWindowOpen(booking),
        onEdit: () => startEditing(section, fields),
        onCancel: cancelEditing,
        onSave: saveDraft
    });

    const renderEventForm = () => {
        const typeId = Number(draft.typeCatLv1);
        const typeCatLv2List = typeCatLv1List.find(item => item.id === typeId)?.categoriesLv2 ?? [];
        const celebrantNameTitle = typeId === 2 ? 'Nombre del bebé' : 'Nombre del festejado/a';
        const selectOptions = {
            dropdownOptions: {
                closeOnClick: true,
                container: document.body
            }
        };

        return <>
            <Select
                id='typeCatLv1'
                name='typeCatLv1'
                multiple={false}
                margin='30px 0 0 0'
                value={ draft.typeCatLv1 }
                onChange={ (e) => changeTypeCatLv1(e.target.value) }
                options={ selectOptions }>
                <option value='' disabled>Tipo de evento*</option>
                { typeCatLv1List.map(item =>
                    <option key={ item.id } value={ String(item.id) }>{ item.title }</option>) }
            </Select>
            { emptyFields.typeCatLv1 && <EmptyFieldMessage/> }
            { [3, 4, 6].includes(typeId) &&
                <Select
                    id='typeCatLv2'
                    name='typeCatLv2'
                    multiple={false}
                    margin='30px 0 0 0'
                    value={ draft.typeCatLv2 }
                    onChange={ (e) => changeDraftField('typeCatLv2', e.target.value) }
                    options={ selectOptions }>
                    <option value=''>Subtipo (Opcional)</option>
                    { typeCatLv2List.map(item =>
                        <option key={ item.id } value={ String(item.id) }>{ item.title }</option>) }
                </Select> }
            { typeId === 1 && <>
                <TextInput
                    id='birthNumber'
                    name='birthNumber'
                    type='number'
                    label='Número de cumpleaños*'
                    placeholder='7'
                    margin='40px 0 0 0'
                    value={ draft.birthNumber }
                    onChange={ (e) => changeDraftField('birthNumber', e.target.value) }/>
                { emptyFields.birthNumber && <EmptyFieldMessage/> }
            </> }
            { [1, 2].includes(typeId) && <>
                <TextInput
                    id='celebrantName'
                    name='celebrantName'
                    type='text'
                    label={ celebrantNameTitle }
                    placeholder='Khaleesi'
                    margin='40px 0 0 0'
                    value={ draft.celebrantName }
                    onChange={ (e) => changeDraftField('celebrantName', e.target.value) }/>
                { emptyFields.celebrantName && <EmptyFieldMessage/> }
                <NameVerificationHint/>
            </> }
            { typeId === 4 && <>
                <TextInput
                    id='couplePerson1Name'
                    name='couplePerson1Name'
                    type='text'
                    label='Pareja. Persona 1. Nombre*'
                    placeholder='Gabriel'
                    margin='40px 0 0 0'
                    value={ draft.couplePerson1Name }
                    onChange={ (e) => changeDraftField('couplePerson1Name', e.target.value) }/>
                { emptyFields.couplePerson1Name && <EmptyFieldMessage/> }
                <NameVerificationHint/>
                <TextInput
                    id='couplePerson2Name'
                    name='couplePerson2Name'
                    type='text'
                    label='Pareja. Persona 2. Nombre*'
                    placeholder='Valentina'
                    margin='40px 0 0 0'
                    value={ draft.couplePerson2Name }
                    onChange={ (e) => changeDraftField('couplePerson2Name', e.target.value) }/>
                { emptyFields.couplePerson2Name && <EmptyFieldMessage/> }
                <NameVerificationHint/>
            </> }
            <div style={{ display: 'flex', margin: '40px 0 0 0' }}>
                <DatePicker
                    id='eventDateDisplay'
                    label='Fecha*'
                    placeholder='26 sept. 2026'
                    options={ eventDatePickerOptions }
                    onChange={ handleEventDateChange }/>
            </div>
            { emptyFields.eventDate && <EmptyFieldMessage/> }
            <div style={{ display: 'flex', margin: '40px 0 0 0' }}>
                <TimePicker
                    id='eventTime'
                    name='eventTime'
                    label='Hora*'
                    placeholder='6:00 PM'
                    defaultValue={ initialDraft.eventTime }
                    options={ eventTimePickerOptions }
                    onInput={ (e) => changeDraftField('eventTime', e.target.value) }/> {/* COVERS TYPING BY HAND */}
            </div>
            { emptyFields.eventTime && <EmptyFieldMessage/> }
            <TextInput
                id='address'
                name='address'
                type='text'
                label='Dirección*'
                placeholder='Av. Pacífico 350'
                margin='40px 0 0 0'
                value={ draft.address }
                onChange={ (e) => changeDraftField('address', e.target.value) }/>
            { emptyFields.address && <EmptyFieldMessage/> }
            <TextInput
                id='placeName'
                name='placeName'
                type='text'
                label='Lugar (Opcional)'
                placeholder='Local Lumaja, Iglesia evangélica, etc'
                margin='40px 0 0 0'
                value={ draft.placeName }
                onChange={ (e) => changeDraftField('placeName', e.target.value) }/>
            { saveError && <ErrorMessage>{ saveError }</ErrorMessage> }
        </>;
    };

    const renderContent = () => {
        if (isBookingLoading || isTypesLoading) return <LoadingSpinner/>;

        if (error || typesError || !booking) {
            return (
                <DetailCard>
                    <Label
                        text_size={SIZE.subtitle}
                        text_color={COLOR.ferra}
                        font_family={FONT_FAMILY.avenirMedium}>
                        { error || typesError
                            ? 'Ocurrió un error al cargar la reserva. Inténtalo nuevamente.'
                            : `No encontramos la reserva ${bookingId}.` }
                    </Label>
                </DetailCard>
            );
        }

        if (isAnOldBooking(booking)) {
            return (
                <DetailCard>
                    <Label
                        text_size={SIZE.subtitle}
                        text_color={COLOR.ferra}
                        font_family={FONT_FAMILY.avenirMedium}>
                        { `La reserva ${bookingId} ya no está disponible porque el evento ya se realizó.` }
                    </Label>
                </DetailCard>
            );
        }

        // Only the fields that apply to the booked event type are filled
        // (celebrant for birthdays, couple names for weddings, ...), so
        // the optional ones are hidden when empty.
        const hasCelebrant = hasValue(booking.celebrantName) || hasValue(booking.birthNumber);
        const hasCouple = hasValue(booking.couplePerson1Name) || hasValue(booking.couplePerson2Name);
        const isCashPayment = hasValue(booking.paymentCashAmount);
        const { typeCatLv1Title, typeCatLv2Title } =
            getTypeTitles(decorationTypes, booking.typeCatLv1Id, booking.typeCatLv2Id);

        console.log(`JE_TEST DATETIME booking.eventDateTime: ${booking.eventDateTime}`)
        console.log(`JE_TEST DATETIME toLocalDateTimeParts: ${toLocalDateTimeParts(booking.eventDateTime)}`)
        const event = toLocalDateTimeParts(booking.eventDateTime);

        return (
            <DetailCard>
                <Label
                    margin='0 0 10px 0'
                    text_size={SIZE.title}
                    text_color={COLOR.ferra}
                    font_family={FONT_FAMILY.avenirMedium}>
                    { `Reserva #${bookingId}` }
                </Label>
                <Detail title='Registrada el' value={formatDateTime(booking.creationDateTime)}/>
                <Label
                    margin='10px 0 0 0'
                    text_size={SIZE.body}
                    text_color={COLOR.secondary}
                    font_family={FONT_FAMILY.avenirMedium}>
                    { `La reserva solo puede editarse hasta ${EDIT_DEADLINE_DAYS} días antes del evento ❗️` }
                </Label>


                <Section>
                    <SectionTitle
                        { ...editProps(SECTION.organizer, buildOrganizerDraft()) }>
                        Organizador
                    </SectionTitle>
                    { editingSection === SECTION.organizer
                        ? <>
                            <TextInput
                                id='organizerName'
                                name='organizerName'
                                type='text'
                                label='Nombre*'
                                placeholder='María Cifuentes'
                                margin='30px 0 0 0'
                                value={ draft.organizerName }
                                onChange={ (e) => changeDraftField('organizerName', e.target.value) }/>
                            { emptyFields.organizerName && <ErrorMessage>Por favor, complete este campo.</ErrorMessage> }
                            <TextInput
                                id='phone'
                                name='phone'
                                type='tel'
                                label='Teléfono / WhatsApp*'
                                placeholder='944555666'
                                margin='40px 0 0 0'
                                value={ draft.phone }
                                onChange={ (e) => changeDraftField('phone', e.target.value) }/>
                            { emptyFields.phone && <ErrorMessage>Por favor, complete este campo.</ErrorMessage> }
                            { saveError && <ErrorMessage>{ saveError }</ErrorMessage> }
                        </>
                        : <>
                            <Detail title='Nombre' value={booking.organizerName}/>
                            <Detail title='Teléfono' value={booking.phone}/>
                        </> }
                </Section>

                <Section>
                    <SectionTitle { ...editProps(SECTION.event, buildEventDraft()) }>
                        Evento
                    </SectionTitle>
                    { editingSection === SECTION.event
                        ? renderEventForm()
                        :
                        <>
                            <Detail title='Tipo' value={typeCatLv1Title}/>
                            { hasValue(typeCatLv2Title) &&
                                <Detail title='Subtipo' value={typeCatLv2Title}/> }
                            { hasValue(booking.birthNumber) &&
                                <Detail title='Cumple' value={booking.birthNumber}/> }
                            { hasCelebrant && <>
                                <Detail title='Celebrado(a)' value={booking.celebrantName}/>
                            </> }
                            { hasCouple && <>
                                <Detail title='Pareja. Persona 1' value={booking.couplePerson1Name}/>
                                <Detail title='Pareja. Persona 2' value={booking.couplePerson2Name}/>
                            </> }
                            <Detail title='Fecha' value={formatDate(event.date)}/>
                            <Detail title='Hora' value={formatTime(event.time)}/>
                            <Detail title='Dirección' value={booking.eventAddress}/>
                            <Detail title='Lugar' value={booking.eventPlace ?? `---`}/>
                        </>
                    }
                </Section>

                <Section>
                    <SectionTitle showEdit={false} isEditDisabled={ editingSection !== null }>Pago</SectionTitle>
                    <Detail title='Método' value={booking.paymentEntity}/>
                    { hasValue(booking.paymentHolder) &&
                        <Detail title='Titular' value={booking.paymentHolder}/> }
                    { hasValue(booking.paymentNumber) &&
                        <Detail title='Número' value={booking.paymentNumber}/> }
                    { isCashPayment &&
                        <Detail title='Monto en efectivo' value={formatCashAmount(booking.paymentCashAmount)}/> }
                    { hasValue(booking.paymentSrc) &&
                        <a href={booking.paymentSrc} target='_blank' rel='noopener noreferrer'>
                            <PaymentImage src={booking.paymentSrc} alt='Captura de pago'/>
                        </a> }
                </Section>
            </DetailCard>
        );
    };

    return (
        <div>
            <Banner title='Detalle de reserva'/>

            <Container>
                <Row margin='40px 0px'>
                    { renderContent() }
                </Row>
            </Container>

            { showSavedModal &&
                <MessageModal onClose={ () => setShowSavedModal(false) }>
                    Has editado tu reserva, nuestro equipo revisará los datos antes de proceder
                </MessageModal> }
        </div>
    );
}

export default BodyBookingDetail;
